import { useEffect, useMemo, useState } from 'react'
import { Link, useLocation, useSearchParams } from 'react-router-dom'
import { useAuthStore } from '@/stores/authStore'
import { useOrgStore } from '@/stores/orgStore'
import { ANON_USER, useSidebarCollapsed } from '@/stores/prefsStore'
import { isReadinessPath, navItemsForPath, type NavItem } from '@/components/layout/navigation'
import { useActiveFeature } from '@/lib/readiness/nav'
import { resolveIndexId } from '@/lib/readiness/profile'
import { isLocked, useIsLocked, useProfileStore } from '@/stores/profileStore'
import { LockedItem } from '@/components/readiness/LockedItem'
import { useReadinessStore } from '@/stores/readinessStore'
import { useMigrationScope } from '@/stores/migrationStore'
import { useCatalogueStore, visibleTechnologies } from '@/stores/catalogueStore'
import { useInfoTooltip } from '@/components/readiness/useInfoTooltip'

// ── Product sidebar (Sidebar Navigation Spec) ───────────────────────────────
// Workspace block (identity, not a switcher) · `Modules` label · one row per
// module · footer with the signed-in user. The items are the platform's own
// (navigation.ts). Two widths: 248px expanded, 68px icon rail; the choice is
// saved per user. Under 900px the sidebar becomes a top strip (CSS, `.qz-nav`)
// that starts collapsed to one row of icon tiles unless the user chose
// otherwise, so the page keeps its height on a phone.

const NARROW = '(max-width: 899.98px)'

const initials = (s: string) =>
  s
    .replace(/@.*$/, '')
    .split(/[\s._-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]!.toUpperCase())
    .join('') || '?'

function useNarrow(): boolean {
  const [narrow, setNarrow] = useState(() => typeof window !== 'undefined' && window.matchMedia(NARROW).matches)
  useEffect(() => {
    const mq = window.matchMedia(NARROW)
    const on = () => setNarrow(mq.matches)
    mq.addEventListener('change', on)
    return () => mq.removeEventListener('change', on)
  }, [])
  return narrow
}

// The Readiness index the item links carry: the URL's `?index=` when valid,
// else the module's last selection (the same rule as `useReadiness`).
function useReadinessIndexId(): string {
  const [params] = useSearchParams()
  const selected = useReadinessStore((s) => s.selectedIndexId)
  const paramIndex = params.get('index')
  const indexLocked = useIsLocked('index_switch')
  return useMemo(() => resolveIndexId(paramIndex, selected, indexLocked), [paramIndex, selected, indexLocked])
}

// Counts the platform already shows beside Readiness items (tracker items,
// catalogue technologies), read straight from the stores so the sidebar never
// triggers the module's own loading. Null hides the count.
function useReadinessCounts(indexId: string): Record<string, number | null> {
  const { currentOrgId: orgId } = useOrgStore()
  const mscope = useMigrationScope(orgId, indexId)
  const techs = useCatalogueStore((s) => s.technologies)
  const catalogue = useMemo(() => visibleTechnologies(techs, orgId).length, [techs, orgId])
  return { tracker: mscope ? mscope.items.length : null, catalogue: catalogue > 0 ? catalogue : null }
}

function Chevron({ className = '' }: { className?: string }) {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false" className={className}>
      <path d="M15 6l-6 6 6 6" />
    </svg>
  )
}

function ModuleIcon({ name }: { name: string }) {
  return (
    <span className="qz-nav-icon material-icons-outlined" aria-hidden="true">
      {name}
    </span>
  )
}

export default function Sidebar() {
  const { pathname } = useLocation()
  const { currentOrgId, orgName } = useOrgStore()
  const { userId, userEmail, role, orgLogo } = useAuthStore()
  const userKey = userId ?? userEmail ?? ANON_USER
  const [savedCollapsed, setCollapsed] = useSidebarCollapsed(userKey)
  const narrow = useNarrow()
  // Expanded by default on desktop (§2); the phone strip starts collapsed.
  const collapsed = savedCollapsed ?? narrow

  const indexId = useReadinessIndexId()
  const counts = useReadinessCounts(indexId)
  const activeFeature = useActiveFeature()
  const items = navItemsForPath(pathname)
  const gates = useProfileStore((s) => s)
  const onReadiness = isReadinessPath(pathname)

  const isActive = (item: NavItem) => (onReadiness ? item.key === activeFeature : item.match(pathname))
  const hrefFor = (item: NavItem) => (item.withIndex ? `${item.route}?index=${indexId}` : item.route)

  const displayName = userEmail ?? 'Signed in'
  const roleLine = [role, orgName].filter(Boolean).join(' · ')

  return (
    <aside id="sidebar" className={`qz-nav${collapsed ? ' is-collapsed' : ''}`} data-collapsed={collapsed ? 'true' : 'false'} data-testid="sidebar">
      <nav aria-label="Product navigation" className="qz-nav-inner">
        {/* Workspace block: identity only, not interactive (§1, §5). */}
        <div className="qz-nav-workspace" data-testid="workspace-block">
          <div className="qz-nav-identity">
            <img className="qz-nav-emblem" src={orgLogo ?? '/logo.png'} alt={orgName} title={currentOrgId} />
            <span className="qz-nav-text qz-nav-workspace-text">
              <span className="qz-nav-workspace-name" title={orgName}>
                {orgName}
              </span>
              <span className="qz-nav-workspace-label">Workspace</span>
            </span>
          </div>
          <button
            type="button"
            className="qz-nav-toggle"
            onClick={() => setCollapsed(!collapsed)}
            aria-expanded={!collapsed}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            data-testid="sidebar-toggle"
          >
            <Chevron className="qz-nav-toggle-icon" />
          </button>
        </div>

        <p className="qz-nav-text qz-nav-section">Modules</p>

        <ul className="qz-nav-modules" data-testid="sidebar-modules">
          {items.map((item) =>
            item.status === 'coming_soon' ? (
              <ComingSoonRow key={item.key} item={item} collapsed={collapsed} />
            ) : (
              <ItemRow key={item.key} item={item} href={hrefFor(item)} collapsed={collapsed} active={isActive(item)} count={onReadiness ? counts[item.key] ?? null : null} locked={!!item.gate && isLocked(gates, item.gate)} />
            )
          )}
        </ul>

        <div className="qz-nav-footer" data-testid="sidebar-footer">
          <span className="qz-nav-avatar" aria-hidden="true">
            {userEmail ? initials(userEmail) : '?'}
          </span>
          <span className="qz-nav-text qz-nav-user">
            <span className="qz-nav-user-name" title={displayName}>
              {displayName}
            </span>
            {roleLine && <span className="qz-nav-user-role">{roleLine}</span>}
          </span>
        </div>
      </nav>
    </aside>
  )
}

function ItemRow({ item, href, collapsed, active, count, locked }: { item: NavItem; href: string; collapsed: boolean; active: boolean; count: number | null; locked: boolean }) {
  if (collapsed) {
    return (
      <li>
        <Link to={href} className={`qz-nav-tile${active ? ' is-active' : ''}`} aria-label={locked ? `${item.label} · locked` : item.label} title={locked ? `${item.label} · available in the full version` : item.label} aria-current={active ? 'page' : undefined} data-testid={`rail-${item.key}`}>
          <ModuleIcon name={item.icon} />
        </Link>
      </li>
    )
  }
  // POC (I6 POC §2): a locked feature row still opens its preview, with a trailing lock.
  if (locked && item.gate) {
    return (
      <li>
        <LockedItem feature={item.gate} variant="drawerItem" to={href} className={`qz-nav-row qz-nav-single${active ? ' is-active' : ''}`} chip={count != null ? <span className="qz-nav-text qz-nav-count">{count}</span> : null} testId={`nav-${item.key}`} linkProps={{ 'aria-current': active ? 'page' : undefined }}>
          <ModuleIcon name={item.icon} />
          <span className="qz-nav-text qz-nav-label">{item.label}</span>
        </LockedItem>
      </li>
    )
  }
  return (
    <li>
      <Link to={href} className={`qz-nav-row qz-nav-single${active ? ' is-active' : ''}`} aria-current={active ? 'page' : undefined} data-testid={`nav-${item.key}`}>
        <ModuleIcon name={item.icon} />
        <span className="qz-nav-text qz-nav-label">{item.label}</span>
        {count != null && <span className="qz-nav-text qz-nav-count">{count}</span>}
      </Link>
    </li>
  )
}

// A `coming_soon` item (I6 §15.1): focusable, inert, dimmed, with a
// full-opacity `Coming soon` chip and an informational tooltip.
function ComingSoonRow({ item, collapsed }: { item: NavItem; collapsed: boolean }) {
  const tip = useInfoTooltip<HTMLButtonElement>({ content: <p>{item.label === 'Settings' ? 'Readiness settings are coming soon.' : `${item.label} is coming soon.`}</p>, width: 240, testId: `coming-soon-tooltip-${item.key}` })
  const trigger = tip.triggerProps as object
  if (collapsed) {
    return (
      <li>
        <button type="button" className="qz-nav-tile is-disabled" aria-disabled="true" aria-label={`${item.label} · coming soon`} title={`${item.label} · coming soon`} onClick={(e) => e.preventDefault()} data-testid={`rail-${item.key}`} {...trigger}>
          <ModuleIcon name={item.icon} />
        </button>
        {tip.node}
      </li>
    )
  }
  return (
    <li>
      <button type="button" className="qz-nav-row qz-nav-single is-disabled" aria-disabled="true" onClick={(e) => e.preventDefault()} data-testid={`drawer-coming-soon-${item.key}`} {...trigger}>
        <span className="qz-nav-dim qz-nav-icon-wrap">
          <ModuleIcon name={item.icon} />
        </span>
        <span className="qz-nav-text qz-nav-label qz-nav-dim">{item.label}</span>
        <span className="qz-nav-text qz-nav-chip" data-testid={`coming-soon-chip-${item.key}`}>
          Coming soon
        </span>
      </button>
      {tip.node}
    </li>
  )
}
